import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import SortDirection, { type SortDirectionType } from '@rio-cloud/rio-uikit/SortDirection';
import TableViewToggles, { type TableViewTogglesViewType } from '@rio-cloud/rio-uikit/TableViewToggles';
import {
    data,
    getCapacityStateFromCapacityStatus,
    getCapacityStatusFromBookingSlots,
    getUserCapacityLabel,
    getUtilizationFromBookingSlots,
    tableConfig,
} from './content';
import type { AppointmentPayload, CapacityStatus, ColumnsConfig, TransformedUser, User } from './content';

export type DemoServiceState = {
    userList: User[];
    activeUser: TransformedUser | null;
    selectedUserIds: string[];
    appointmentAnimationUserIds: string[];
    sortBy: ColumnsConfig['columnOrder'][number];
    sortDir: SortDirectionType;
    chunks: number;
    columns: ColumnsConfig;
    capacityFilter: CapacityStatus[];
    search: string;
    viewType: TableViewTogglesViewType;
    showConfirmDelete: boolean;
    showCreateAppointment: boolean;
    showCreateUserAppointment: boolean;
    showCreateSingleAppointment: boolean;
    isPrintSidebar: boolean;
    isPrintTable: boolean;
};

export const defaultAppState: DemoServiceState = {
    userList: data,
    activeUser: null,
    selectedUserIds: [],
    appointmentAnimationUserIds: [],
    sortBy: tableConfig.defaultSortKey,
    sortDir: SortDirection.ASCENDING,
    chunks: 1,
    columns: {
        columnOrder: tableConfig.defaultColumnOrder,
        hiddenColumns: tableConfig.defaultHiddenColumns,
        columnsDetails: tableConfig.defaultColumnsDetails,
    },
    capacityFilter: [],
    search: '',
    viewType: TableViewToggles.VIEW_TYPE_TABLE,
    showConfirmDelete: false,
    showCreateAppointment: false,
    showCreateUserAppointment: false,
    showCreateSingleAppointment: false,
    isPrintSidebar: false,
    isPrintTable: false,
};

const scheduleUserAppointment = <TUser extends User | TransformedUser>(
    user: TUser,
    appointment: AppointmentPayload
): TUser => {
    const bookingSlotsBooked = Math.min(user.bookingSlotsBooked + 1, user.bookingSlotsTotal);
    const capacityStatus = getCapacityStatusFromBookingSlots(bookingSlotsBooked);
    const percent = getUtilizationFromBookingSlots(bookingSlotsBooked, user.bookingSlotsTotal);
    const nextUser = {
        ...user,
        bookingSlotsBooked,
        nextAppointment: appointment.nextAppointment,
        nextAppointmentTopic: appointment.topic,
        nextAppointmentLocation: appointment.location,
        capacityState: getCapacityStateFromCapacityStatus(capacityStatus),
        userCapacity: {
            percent,
            status: capacityStatus,
        },
    };

    if ('dataKey' in nextUser) {
        return {
            ...nextUser,
            userCapacityLabel: getUserCapacityLabel(capacityStatus, bookingSlotsBooked, user.bookingSlotsTotal),
            userCapacityValue: percent,
        };
    }

    return nextUser;
};

const setUserCapacityStatus = <TUser extends User | TransformedUser>(
    user: TUser,
    capacityStatus: CapacityStatus
): TUser => {
    const percent = getUtilizationFromBookingSlots(user.bookingSlotsBooked, user.bookingSlotsTotal);
    const nextUser = {
        ...user,
        capacityState: getCapacityStateFromCapacityStatus(capacityStatus),
        userCapacity: {
            percent,
            status: capacityStatus,
        },
    };

    if ('dataKey' in nextUser) {
        return {
            ...nextUser,
            userCapacityLabel: getUserCapacityLabel(capacityStatus, user.bookingSlotsBooked, user.bookingSlotsTotal),
            userCapacityValue: percent,
        };
    }

    return nextUser;
};

export const demoServiceSlice = createSlice({
    name: 'demoService',
    initialState: defaultAppState,
    reducers: {
        deleteUsers: (state, action: PayloadAction<string[]>) => {
            const userIds = new Set(action.payload || []);
            state.userList = state.userList.filter(user => !userIds.has(user.itemId));
            state.activeUser = null;
        },
        scheduleAppointments: (
            state,
            action: PayloadAction<{ userIds: string[]; appointment: AppointmentPayload }>
        ) => {
            const userIds = new Set(action.payload.userIds || []);
            state.appointmentAnimationUserIds = action.payload.userIds || [];
            state.userList = state.userList.map(user =>
                userIds.has(user.itemId) ? scheduleUserAppointment(user, action.payload.appointment) : user
            );

            if (state.activeUser && userIds.has(state.activeUser.dataKey)) {
                state.activeUser = scheduleUserAppointment(state.activeUser, action.payload.appointment);
            }
        },
        setVacationStatus: (state, action: PayloadAction<{ userId: string; onVacation: boolean }>) => {
            const updateUser = <TUser extends User | TransformedUser>(user: TUser) => {
                const capacityStatus = action.payload.onVacation
                    ? 'vacation'
                    : getCapacityStatusFromBookingSlots(user.bookingSlotsBooked);

                return setUserCapacityStatus(user, capacityStatus);
            };

            state.userList = state.userList.map(user =>
                user.itemId === action.payload.userId ? updateUser(user) : user
            );

            if (state.activeUser?.dataKey === action.payload.userId) {
                state.activeUser = updateUser(state.activeUser);
            }
        },
        selectedUserIds: (state, action: PayloadAction<string[]>) => {
            state.selectedUserIds = action.payload || [];
        },
        clearAppointmentAnimationUserIds: state => {
            state.appointmentAnimationUserIds = [];
        },
        showConfirmDelete: (state, action: PayloadAction<boolean>) => {
            state.showConfirmDelete = Boolean(action.payload);
        },
        showCreateAppointment: (state, action: PayloadAction<boolean>) => {
            state.showCreateAppointment = Boolean(action.payload);
        },
        showCreateUserAppointment: (state, action: PayloadAction<boolean>) => {
            state.showCreateUserAppointment = Boolean(action.payload);
        },
        showCreateSingleAppointment: (state, action: PayloadAction<boolean>) => {
            state.showCreateSingleAppointment = Boolean(action.payload);
        },
        activeUser: (state, action: PayloadAction<TransformedUser | null>) => {
            state.activeUser = action.payload ?? null;
        },
        printSidebar: (state, action: PayloadAction<boolean>) => {
            state.isPrintSidebar = Boolean(action.payload);
        },
        printTable: (state, action: PayloadAction<boolean>) => {
            state.isPrintTable = Boolean(action.payload);
        },
        setSearch: (state, action: PayloadAction<string>) => {
            state.search = action.payload;
        },
        setSort: (
            state,
            action: PayloadAction<{ sortBy: ColumnsConfig['columnOrder'][number]; sortDir: SortDirectionType }>
        ) => {
            state.sortBy = action.payload.sortBy;
            state.sortDir = action.payload.sortDir;
        },
        setChunks: (state, action: PayloadAction<number>) => {
            state.chunks = action.payload;
        },
        setViewType: (state, action: PayloadAction<TableViewTogglesViewType>) => {
            state.viewType = action.payload;
        },
        setColumns: (state, action: PayloadAction<ColumnsConfig>) => {
            state.columns = action.payload;
        },
        setCapacityFilter: (state, action: PayloadAction<CapacityStatus[]>) => {
            state.capacityFilter = action.payload;
        },
    },
});

export const actions = demoServiceSlice.actions;

export default demoServiceSlice.reducer;
